import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { campanhaEditavel, enviarVista3d, gravarLinhasDeCorte, tirarVista3d, verVertical, type CampanhaCompleta, type LinhaDeCorte } from '../../api/cptu';
import { GRANDEZAS, GRANDEZAS_DA_ESTATISTICA, comUnidadePelaZona, planoLocal, sugerirPorTRatio } from '../../cptu/motor';
import type { Grandeza, ModeloDeVariograma } from '../../cptu/motor';
import {
  AFASTAMENTO_DA_LINHA, CANCELADO, ControleDasSobreposicoes, GRANDEZAS_DA_SECAO, GRANDEZAS_DA_SOBREPOSICAO,
  GraficoDoVariograma, LEGENDA_DO_ESPACIAL, LINHAS_NO_MAXIMO, Modelo3d, OPACIDADE_DA_SOBREPOSICAO, PONTOS_POR_LINHA,
  ESCALA_DA_SOBREPOSICAO, PerfisDaCampanha, SecaoEspacial, calcularNoTrabalhador, combinarMarcas, grandezasDaSecao,
  limitesDoTerreno, marcaDaColuna, numeroBr, passosPadrao, pedidoDaGrade, pedidoDaSecao, proximoNome, separarVerticais,
  sobreposicoesDasVerticais, CHAVE_DAS_ESCALAS, chaveDasEscalas, comEscala, gravarEscalas, lerEscalasDe, ajustesDo3dValidos, sistemaDaMalha,
} from '../../cptu/visual';
import type {
  AjustesDo3d, CamadaNoModelo, CamadasDoModelo, ControleDoModelo3d, FonteDasCamadas, EscalaDoGrafico, EstadoDasSobreposicoes, GrandezaDaSecao, GrandezaDaSobreposicao, ImagemDoModelo, MarcaDeHomologacao, RespostaDaGradeDoTrabalhador,
  RespostaDaSecao, RespostaDoTrabalhador, VerticalDaAnalise,
} from '../../cptu/visual';
import type { LinhaDoMapa } from '../../mapaGeotecnico';
import { plural } from '../../formato';
import { useSessao } from '../../sessao';
import { AcaoBloqueada, Area, Aviso, Dialogo, ErroDaApi, useEstreito } from '../../ui';
import { rotaDaAnalise, useAnaliseDaCampanha, verticaisAtivas, type VistaDaAnalise } from './analise';
import { PERMISSAO_DE_EDITAR_CPTU, motivoDaCampanhaParada } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';
import { ESPERA_DO_RECALCULO, rotaDaVertical, useFocoDeVolta } from './interpretacao';
import { MapaDaCampanha, type VerticeDaLinha } from './MapaDaCampanha';
import { ouvirParametrosDoTRatio, parametrosDoTRatioGuardados } from './parametrosDasCamadas';
import { PerfilTipico } from './PerfilTipico';
import { usePincaDoModelo3d } from './pincaDoModelo3d';
import { TAMANHO_DA_CAPTURA, comporAVista, linhasDoMetodo } from './vistaParaORelatorio';

/**
 * A aba Análise integrada da campanha CPTu — onda 316, Tarefa 316.2 (R2–R10; spec 07 §5.7 e §11; a prancheta «5 ·
 * Análise integrada»). MODELO INTERPRETATIVO, fora do RN-252: entre as verticais o valor é estimado, e a legenda fixa
 * vai no topo da aba e em cada desenho.
 *
 * Duas vistas com rota (o molde das vistas da vertical, `nav.cptu-vistas`):
 *  · Mapa e seção — o mapa com a linha de corte (cliques nas verticais, vértices livres, ou «De … Até …»), a seção 2D
 *    com o cursor, as faixas das verticais por cima (o pedido do Humberto de 16/10) e o variograma embaixo. Traçar é
 *    consulta (`cptu.ver`); «Gravar a linha A–B» pede motivo e grava pelo PUT, só na campanha em rascunho ou devolvida
 *    — fora disso, à vista e bloqueada com o motivo (P-UI-24). No celular (até 640 px) só se consulta: as linhas
 *    gravadas em fichas (RN-251).
 *  · Perfis — as verticais sobrepostas (com a média ± desvio) ou lado a lado, e o perfil típico pelas camadas.
 *  · 3D (Tarefa 316.3, passos 6–10) — `/cptu/:id/analise/3d`: o three.js e a grade só descem quando a vista abre
 *    (no celular, só depois de «Abrir o modelo 3D»); a grade do worker e, uma a uma, as seções das linhas gravadas
 *    (a cerca), com `await` — o worker tem uma fila só, e o pedido cancelado por outro se refaz; o terreno com a
 *    imagem do satélite (`montarImagemDoTerreno`, sob demanda); e os gráficos das verticais como fitas, com o MESMO
 *    painel da seção e o mesmo estado guardado (trocar de vista mantém as escolhas). Com menos de três verticais
 *    fora de uma linha, a frase do R4 no lugar do modelo.
 *
 * Quem fica de fora diz por quê: «sem coordenada», «sem cota», e as verticais no mesmo lugar, que entram pela média.
 */
export function CampanhaAnalise({ c, vista, aoMudar }: { c: CampanhaCompleta; vista: VistaDaAnalise; aoMudar(): Promise<void> }) {
  const { verticais, erro } = useAnaliseDaCampanha(c);
  const estreito = useEstreito();
  const [busca] = useSearchParams();
  const linha = busca.get('linha');
  const separadas = useMemo(() => (verticais ? separarVerticais(verticais) : null), [verticais]);
  const idDe = useMemo(() => new Map(verticaisAtivas(c).map((v) => [v.codigo, v.id])), [c]);

  return (
    <section className="cptu-analise" aria-label="Análise integrada">
      <div className="cptu-analise-topo">
        <nav className="cptu-vistas" aria-label="Vistas da análise">
          {([['mapa', 'Mapa e seção'], ['perfis', 'Perfis'], ['3d', '3D']] as const).map(([v, texto]) => (
            <Link key={v} to={rotaDaAnalise(c.campanha.id, v, linha)} aria-current={vista === v ? 'page' : undefined}
              className={vista === v ? 'sel' : undefined}>
              {texto}
            </Link>
          ))}
        </nav>
        <p className="cptu-legenda-fixa">{LEGENDA_DO_ESPACIAL}.</p>
        {/* Onda 316 (316.6, RN-251): no celular a aba é de consulta — a linha de corte se traça e se grava no computador. */}
        {estreito && <p className="cptu-nota cptu-analise-so-consulta">{AVISO_DO_CELULAR}</p>}
      </div>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {!verticais || !separadas ? (
        <p className="cptu-nota" role="status">Calculando as verticais da campanha…</p>
      ) : (
        <>
          <QuemFica fora={separadas.fora} noMesmoLugar={separadas.noMesmoLugar} uteis={separadas.uteis.length}
            vista={vista} rota={(codigo) => {
              const id = idDe.get(codigo);
              return id ? rotaDaVertical(c.campanha.id, id) : null;
            }} />
          {vista === 'mapa' && separadas.uteis.length >= 2 && (
            <VistaMapaESecao c={c} verticais={verticais} uteis={separadas.uteis} aoMudar={aoMudar} />
          )}
          {vista === 'perfis' && <VistaPerfis c={c} verticais={verticais} uteis={separadas.uteis} />}
          {vista === '3d' && <Vista3d c={c} verticais={verticais} uteis={separadas.uteis} aoMudar={aoMudar} />}
        </>
      )}
    </section>
  );
}

/** O aviso do celular, curto (316.6): só consulta. */
const AVISO_DO_CELULAR = 'No celular só se consulta: traçar e gravar a linha de corte é no computador.';

/* ── Quem fica de fora ──────────────────────────────────────────────── */

/** «CPTu09 e CPTu10», «CPTu01, CPTu02 e CPTu03». */
const comE = (xs: readonly string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

function QuemFica({ fora, noMesmoLugar, uteis, vista, rota }: {
  fora: readonly { codigo: string; motivo: string }[];
  noMesmoLugar: readonly string[][];
  uteis: number;
  vista: VistaDaAnalise;
  rota(codigo: string): string | null;
}) {
  const lista = (xs: readonly { codigo: string; motivo: string }[]) => xs.map((f, i) => {
    const para = rota(f.codigo);
    return (
      <span key={f.codigo}>
        {i > 0 ? ', ' : ''}
        {para ? <Link to={para}>{f.codigo}</Link> : f.codigo} ({f.motivo})
      </span>
    );
  });
  return (
    <div className="cptu-analise-quem">
      {vista === 'mapa' && uteis < 2 ? (
        <Aviso tipo="atencao" titulo="A análise espacial precisa de duas verticais com coordenada e cota.">
          <span className="cptu-analise-faltam">— faltam: {lista(fora)}.</span>{' '}
          A coordenada e a cota se informam na ficha de cada vertical. A vista Perfis funciona por profundidade.
        </Aviso>
      ) : fora.length > 0 ? (
        <p className="cptu-nota cptu-analise-fora">Fora da análise espacial: {lista(fora)}.</p>
      ) : null}
      {noMesmoLugar.map((g) => (
        <p key={g.join()} className="cptu-nota cptu-analise-mesmo-lugar">{comE(g)} no mesmo lugar: entram pela média.</p>
      ))}
    </div>
  );
}

/* ── A linha de corte: o comprimento e as pontas ─────────────────────── */

/** O comprimento de uma polilinha, m (no plano local dela). */
function comprimentoDa(pontos: readonly { latitude: number; longitude: number }[]): number {
  if (pontos.length < 2) return 0;
  const plano = planoLocal(pontos);
  let total = 0;
  for (let i = 1; i < pontos.length; i++) {
    const a = plano.paraPlano(pontos[i - 1].latitude, pontos[i - 1].longitude);
    const b = plano.paraPlano(pontos[i].latitude, pontos[i].longitude);
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

/** O código da vertical a até 1 cm de um ponto, ou «ponto livre». */
function nomeDoPonto(p: { latitude: number; longitude: number }, vs: readonly VerticalDaAnalise[]): string {
  const comCoordenada = vs.filter((v) => v.latitude !== null && v.longitude !== null);
  const plano = planoLocal([p, ...comCoordenada.map((v) => ({ latitude: v.latitude as number, longitude: v.longitude as number }))]);
  const a = plano.paraPlano(p.latitude, p.longitude);
  const v = comCoordenada.find((x) => {
    const b = plano.paraPlano(x.latitude as number, x.longitude as number);
    return Math.hypot(b.x - a.x, b.y - a.y) <= 0.01;
  });
  return v ? v.codigo : 'ponto livre';
}

/** A ordem das verticais ao longo de uma linha (as a até 30 m dela, pela distância), e as outras pelo código. */
function ordemNaLinha(vs: readonly VerticalDaAnalise[], pontos: readonly { latitude: number; longitude: number }[]): string[] {
  const porCodigo = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });
  const uteis = vs.filter((v) => v.latitude !== null && v.longitude !== null);
  if (pontos.length < 2 || !uteis.length) return vs.map((v) => v.codigo).sort(porCodigo);
  const plano = planoLocal(pontos);
  const linha = pontos.map((q) => plano.paraPlano(q.latitude, q.longitude));
  const projetadas: { codigo: string; d: number }[] = [];
  for (const v of uteis) {
    const p = plano.paraPlano(v.latitude as number, v.longitude as number);
    let melhor = { afastamento: Infinity, d: 0 };
    let acumulado = 0;
    for (let s = 1; s < linha.length; s++) {
      const a = linha[s - 1], b = linha[s];
      const dx = b.x - a.x, dy = b.y - a.y;
      const l2 = dx * dx + dy * dy;
      const t = l2 > 0 ? Math.min(Math.max(((p.x - a.x) * dx + (p.y - a.y) * dy) / l2, 0), 1) : 0;
      const afastamento = Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
      if (afastamento < melhor.afastamento) melhor = { afastamento, d: acumulado + t * Math.sqrt(l2) };
      acumulado += Math.sqrt(l2);
    }
    if (melhor.afastamento <= AFASTAMENTO_DA_LINHA) projetadas.push({ codigo: v.codigo, d: melhor.d });
  }
  projetadas.sort((a, b) => a.d - b.d || porCodigo(a.codigo, b.codigo));
  const naLinha = projetadas.map((x) => x.codigo);
  return [...naLinha, ...vs.map((v) => v.codigo).filter((c) => !naLinha.includes(c)).sort(porCodigo)];
}

/** «120,00 m». */
const metrosBr = (v: number) => `${numeroBr(v, 2)} m`;

/* ── As escolhas da vista guardadas no aparelho (por pessoa; nunca o que precisa durar) ── */

/**
 * Os gráficos sobre as verticais (as sobreposições), no aparelho e POR USUÁRIO (os celulares do laboratório são
 * compartilhados): `labmanager.cptu.analise.sobreposicoes.<id>`, a regra de chave das escalas (`chaveDasEscalas`). A chave
 * sem o id (de antes da 347) não passa a ninguém: herdá-la seria dar a escolha de um ao primeiro que abrir a tela.
 */
const CHAVE_DAS_SOBREPOSICOES = 'labmanager.cptu.analise.sobreposicoes';

function sobreposicoesGuardadas(quem: string | null): EstadoDasSobreposicoes {
  const padrao: EstadoDasSobreposicoes = { grandeza: null, desligadas: [], opacidade: OPACIDADE_DA_SOBREPOSICAO, escala: ESCALA_DA_SOBREPOSICAO };
  try {
    const bruto = localStorage.getItem(chaveDasEscalas(CHAVE_DAS_SOBREPOSICOES, quem));
    if (!bruto) return padrao;
    const x = JSON.parse(bruto) as Partial<EstadoDasSobreposicoes>;
    const grandeza = GRANDEZAS_DA_SOBREPOSICAO.some((g) => g.chave === x.grandeza) ? x.grandeza as GrandezaDaSobreposicao : null;
    const opacidade = typeof x.opacidade === 'number' && x.opacidade >= 0 && x.opacidade <= 1 ? x.opacidade : padrao.opacidade;
    return {
      grandeza, opacidade, escala: x.escala === 'propria' ? 'propria' : 'comum',
      desligadas: Array.isArray(x.desligadas) ? x.desligadas.filter((c): c is string => typeof c === 'string') : [],
    };
  } catch { return padrao; }
}

function guardarSobreposicoes(quem: string | null, e: EstadoDasSobreposicoes): void {
  try { localStorage.setItem(chaveDasEscalas(CHAVE_DAS_SOBREPOSICOES, quem), JSON.stringify(e)); } catch { /* sem memória no aparelho: vale até sair */ }
}

/** As sobreposições de quem está na sessão: lidas na chave dele, relidas quando ele troca, gravadas na dele. */
function useSobreposicoes(): [EstadoDasSobreposicoes, (e: EstadoDasSobreposicoes) => void] {
  const quem = useSessao().eu?.usuarioId ?? null;
  const [sobre, setSobre] = useState<EstadoDasSobreposicoes>(() => sobreposicoesGuardadas(quem));
  useEffect(() => { setSobre(sobreposicoesGuardadas(quem)); }, [quem]);
  const mudar = useCallback((e: EstadoDasSobreposicoes) => { setSobre(e); guardarSobreposicoes(quem, e); }, [quem]);
  return [sobre, mudar];
}

/**
 * Os ajustes da barra do 3D (onda 347, §4), no aparelho e POR USUÁRIO (os celulares do laboratório são compartilhados):
 * `labmanager.cptu.analise.3d.<id>`, a mesma regra de chave das escalas (`chaveDasEscalas`). O que vier passa pela
 * `ajustesDo3dValidos` (fora da faixa → o de partida).
 */
const CHAVE_DOS_AJUSTES_DO_3D = 'labmanager.cptu.analise.3d';

function ajustesDo3dGuardados(quem: string | null): AjustesDo3d {
  try {
    const bruto = localStorage.getItem(chaveDasEscalas(CHAVE_DOS_AJUSTES_DO_3D, quem));
    return ajustesDo3dValidos(bruto ? JSON.parse(bruto) : null);
  } catch { return ajustesDo3dValidos(null); }
}

function guardarAjustesDo3d(quem: string | null, a: AjustesDo3d): void {
  try { localStorage.setItem(chaveDasEscalas(CHAVE_DOS_AJUSTES_DO_3D, quem), JSON.stringify(a)); } catch { /* sem memória no aparelho: vale até sair */ }
}

/** O motivo do «Guardar esta vista» com a sugestão de camadas no 3D (RN-269). */
const MOTIVO_DA_SUGESTAO_NA_VISTA = 'a vista com a sugestão de camadas não vai ao relatório (RN-269)';

/**
 * As camadas VIGENTES das verticais ativas que têm camada (o `GET /cptu/verticais/:id`, como o perfil típico), pelo código; null
 * enquanto lê. Relê só quando muda o conjunto das verticais com camada ou quantas cada uma tem.
 */
function useCamadasGravadas(c: CampanhaCompleta, ligado: boolean): { camadas: ReadonlyMap<string, CamadaNoModelo[]> | null; erro: unknown } {
  const [camadas, setCamadas] = useState<ReadonlyMap<string, CamadaNoModelo[]> | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const comCamadas = verticaisAtivas(c).filter((v) => v.n_camadas > 0);
  const chave = comCamadas.map((v) => `${v.id}:${v.n_camadas}`).join('|');
  useEffect(() => {
    if (!ligado) return undefined;
    let vivo = true;
    setErro(null);
    if (!comCamadas.length) { setCamadas(new Map()); return undefined; }
    void (async () => {
      try {
        const lidas = await Promise.all(comCamadas.map((v) => verVertical(v.id)));
        if (vivo) setCamadas(new Map(lidas.map((v) => [v.codigo, v.camadas.map((k) => ({ topo: k.topo_m, base: k.base_m, unidade: k.unidade }))])));
      } catch (e) { if (vivo) setErro(e); }
    })();
    return () => { vivo = false; };
    // A chave resume as verticais com camadas e quantas: o mesmo conjunto não relê.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, ligado]);
  return { camadas, erro };
}

/**
 * A escala horizontal dos gráficos (onda 347, R12): por grandeza, guardada neste aparelho (a mesma chave da aba
 * Verticais: a escala vale para a grandeza em todos os gráficos de resultados); não muda o relatório.
 */
function useEscalas(): [Record<string, EscalaDoGrafico>, (g: string, e: EscalaDoGrafico | null) => void] {
  // Por usuário: os celulares do laboratório são compartilhados (§4.8).
  const quem = useSessao().eu?.usuarioId ?? null;
  const [escalas, setEscalas] = useState<Record<string, EscalaDoGrafico>>(() => lerEscalasDe(CHAVE_DAS_ESCALAS, quem));
  const mudar = useCallback((g: string, e: EscalaDoGrafico | null) => {
    setEscalas((atuais) => {
      const novas = comEscala(atuais, g, e);
      gravarEscalas(chaveDasEscalas(CHAVE_DAS_ESCALAS, quem), novas);
      return novas;
    });
  }, [quem]);
  return [escalas, mudar];
}

/** O domínio desenhado das faixas (o menor início e o maior fim): o «Fixo» da janela da escala começa nele. */
function dominioDasFaixas(d: { verticais: readonly { dominio: readonly [number, number] }[] } | null): [number, number] | null {
  if (!d || d.verticais.length === 0) return null;
  return [Math.min(...d.verticais.map((v) => v.dominio[0])), Math.max(...d.verticais.map((v) => v.dominio[1]))];
}

/* ── Mapa e seção ───────────────────────────────────────────────────── */

const NOME_DO_MODELO: Record<ModeloDeVariograma, string> = { esferico: 'esférica', exponencial: 'exponencial' };

function VistaMapaESecao({ c, verticais, uteis, aoMudar }: {
  c: CampanhaCompleta; verticais: readonly VerticalDaAnalise[]; uteis: readonly VerticalDaAnalise[]; aoMudar(): Promise<void>;
}) {
  const { pode, eu } = useSessao();
  const estreito = useEstreito();
  const navegar = useNavigate();
  const [busca] = useSearchParams();
  const linhas: LinhaDeCorte[] = useMemo(() => c.campanha.linhas_corte ?? [], [c.campanha.linhas_corte]);
  const nomeGravado = busca.get('linha');
  const gravada = nomeGravado ? linhas.find((l) => l.nome === nomeGravado) ?? null : null;

  const [vertices, setVertices] = useState<VerticeDaLinha[]>([]);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [grandeza, setGrandeza] = useState<GrandezaDaSecao>('sbtn');
  const [metodo, setMetodo] = useState<'idw' | 'krigagem'>(gravada?.metodo ?? 'krigagem');
  const [modelo, setModelo] = useState<ModeloDeVariograma>(gravada?.modelo ?? 'esferico');
  const [comIsolinhas, setComIsolinhas] = useState(true);
  const [sobre, mudarSobre] = useSobreposicoes();
  /* A resposta e a grandeza com que ela foi pedida: enquanto a nova não chega, a seção velha se desenha com a dela. */
  const [resposta, setResposta] = useState<RespostaDoTrabalhador | null>(null);
  const [grandezaDaResposta, setGrandezaDaResposta] = useState<GrandezaDaSecao>('sbtn');
  const [calculando, setCalculando] = useState(false);
  const [erroDaConta, setErroDaConta] = useState<unknown>(null);
  const [janela, setJanela] = useState<{ tipo: 'gravar' } | { tipo: 'tirar'; nome: string } | null>(null);

  /* Escolher uma linha gravada traz o método e o modelo com que ela foi gravada (o PDF desenha com eles). */
  useEffect(() => {
    if (gravada) { setMetodo(gravada.metodo); setModelo(gravada.modelo); }
  }, [gravada?.nome]); // eslint-disable-line react-hooks/exhaustive-deps

  const [escalas, mudarEscala] = useEscalas();
  const eixoDaSobreposicao = sobre.grandeza ? escalas[sobre.grandeza] ?? null : null;

  const tracar = !estreito;
  const provisoria = tracar && vertices.length >= 2;
  const pontos = provisoria ? vertices.map((v) => ({ latitude: v.latitude, longitude: v.longitude })) : gravada?.pontos ?? null;
  const nomeDaNova = proximoNome(linhas);
  const nome = provisoria ? nomeDaNova : gravada?.nome ?? null;
  const comprimento = pontos ? comprimentoDa(pontos) : 0;
  const passos = passosPadrao({ comprimento, celular: estreito });
  const chaveDaLinha = pontos ? pontos.map((p) => `${p.latitude},${p.longitude}`).join(';') : '';

  /* A conta vai ao worker 150 ms depois da última mudança; o último pedido vence. */
  useEffect(() => {
    if (!pontos) { setResposta(null); setCalculando(false); return undefined; }
    setCalculando(true);
    let cancelar: (() => void) | null = null;
    let vivo = true;
    const espera = setTimeout(() => {
      const pedido = pedidoDaSecao(uteis, {
        linha: pontos, grandeza, metodo, modelo, passoHorizontal: passos.secao.horizontal, passoVertical: passos.secao.vertical,
      });
      const t = calcularNoTrabalhador(pedido);
      cancelar = t.cancelar;
      t.resposta.then((r) => {
        if (!vivo) return;
        setResposta(r); setGrandezaDaResposta(pedido.grandeza); setErroDaConta(null); setCalculando(false);
      }, (e: unknown) => {
        if (!vivo || (e instanceof Error && e.message === CANCELADO)) return;
        setErroDaConta(e); setCalculando(false);
      });
    }, ESPERA_DO_RECALCULO);
    return () => { vivo = false; clearTimeout(espera); cancelar?.(); };
    // A chave resume a linha; os passos saem dela e do celular.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDaLinha, grandeza, metodo, modelo, uteis, passos.secao.horizontal, passos.secao.vertical]);

  /* As linhas no mapa: as gravadas contínuas, a provisória tracejada (só o traço que já tem dois pontos). */
  const linhasDoMapa: LinhaDoMapa[] = useMemo(() => {
    const saida: LinhaDoMapa[] = linhas.map((l) => ({
      chave: l.nome, provisoria: false, pontos: l.pontos.map((p) => [p.latitude, p.longitude] as [number, number]),
      rotulos: l.nome.split('–'),
    }));
    if (tracar && vertices.length >= 1) {
      saida.push({
        chave: 'provisoria', provisoria: true, pontos: vertices.map((v) => [v.latitude, v.longitude] as [number, number]),
        rotulos: (nomeDaNova ?? '–').split('–'),
      });
    }
    return saida;
  }, [linhas, vertices, tracar, nomeDaNova]);

  const verticesNoMapa = useMemo(() => verticaisAtivas(c)
    .filter((v) => uteis.some((u) => u.codigo === v.codigo))
    .map((v) => ({ id: v.id, codigo: v.codigo, latitude: v.latitude as number, longitude: v.longitude as number })), [c, uteis]);

  const porCodigo = useMemo(() => new Map(uteis.map((v) => [v.codigo, v])), [uteis]);
  const acrescentar = useCallback((v: VerticeDaLinha) => {
    setVertices((antes) => {
      const ultimo = antes[antes.length - 1];
      // Sem ponto repetido em seguida (o esquema recusa), e no máximo 20.
      if (ultimo && ultimo.latitude === v.latitude && ultimo.longitude === v.longitude) return antes;
      if (antes.length >= PONTOS_POR_LINHA.maximo) return antes;
      return [...antes, v];
    });
    if (nomeGravado) navegar(rotaDaAnalise(c.campanha.id, 'mapa'), { replace: true });
  }, [nomeGravado, navegar, c.campanha.id]);

  const deAte = (a: string, b: string) => {
    setDe(a); setAte(b);
    const va = porCodigo.get(a), vb = porCodigo.get(b);
    if (va && vb && a !== b) {
      setVertices([
        { latitude: va.latitude as number, longitude: va.longitude as number, codigo: a },
        { latitude: vb.latitude as number, longitude: vb.longitude as number, codigo: b },
      ]);
      if (nomeGravado) navegar(rotaDaAnalise(c.campanha.id, 'mapa'), { replace: true });
    }
  };

  /* A gravação: permissão e campanha parada à vista (P-UI-24). */
  const editavel = campanhaEditavel(c.campanha.situacao);
  const podeEditar = pode('cptu.editar');
  let acaoGravar: ReactNode = null;
  if (provisoria && nomeDaNova) {
    const texto = `Gravar a linha ${nomeDaNova}`;
    acaoGravar = !podeEditar ? (
      <AcaoBloqueada texto={texto} oQueFaz="não grava a linha de corte" permissao={PERMISSAO_DE_EDITAR_CPTU}
        persona={eu?.atuandoPor ? eu.nome : null} />
    ) : !editavel ? (
      <AcaoBloqueada texto={texto} motivo={motivoDaCampanhaParada(c.campanha.situacao)} />
    ) : (
      <button type="button" className="botao" data-abre="gravar-linha" onClick={() => setJanela({ tipo: 'gravar' })}>{texto}</button>
    );
  } else if (provisoria && !nomeDaNova) {
    acaoGravar = <AcaoBloqueada texto="Gravar a linha" motivo={`A campanha já tem ${LINHAS_NO_MAXIMO} linhas de corte: tire uma para gravar outra.`} />;
  }

  const inicio = pontos ? (provisoria ? vertices[0].codigo ?? 'ponto livre' : nomeDoPonto(pontos[0], verticais)) : '';
  const fim = pontos ? (provisoria ? vertices[vertices.length - 1].codigo ?? 'ponto livre' : nomeDoPonto(pontos[pontos.length - 1], verticais)) : '';
  // Enquanto recalcula, a seção velha fica à vista (com «Calculando a seção…»), mas a frase do corte espera a nova.
  const respostaAtual = resposta;
  const quantas = resposta && !calculando ? resposta.estacoes.length : null;

  const ofertas = useMemo(() => grandezasDaSecao(uteis, { mostrarNaoHomologadas: true }), [uteis]);
  const marcaDaGrandeza = ofertas.find((g) => g.chave === grandeza)?.marca ?? null;
  const marcasDasSobreposicoes = useMemo(() => {
    const m: Partial<Record<GrandezaDaSobreposicao, MarcaDeHomologacao>> = {};
    for (const g of GRANDEZAS_DA_SOBREPOSICAO) m[g.chave] = combinarMarcas(uteis.map((v) => ({ marca: marcaDaColuna(g.coluna, v.r.premissas) })));
    return m;
  }, [uteis]);
  const estacoes = respostaAtual ? [...new Set(respostaAtual.estacoes.map((e) => e.codigo))] : [];
  const dadosDasSobreposicoes = useMemo(
    () => (sobre.grandeza && estacoes.length
      ? sobreposicoesDasVerticais(verticais, { grandeza: sobre.grandeza, codigos: estacoes, escala: sobre.escala, eixo: eixoDaSobreposicao })
      : null),
    // As estações resumidas pelo texto: a mesma lista não refaz as faixas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verticais, sobre.grandeza, sobre.escala, estacoes.join(), eixoDaSobreposicao],
  );
  const ligadas = useMemo(() => new Set(estacoes.filter((e) => !sobre.desligadas.includes(e))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estacoes.join(), sobre.desligadas]);

  return (
    <div className="cptu-analise-mapa-e-secao">
      <div className="cptu-analise-colunas">
        <section className="cptu-analise-lado-mapa" aria-label="Mapa e linha de corte">
          <MapaDaCampanha verticais={verticesNoMapa} linhas={linhasDoMapa} cliente={c.os.cliente ?? ''} tracar={tracar}
            aoVertice={acrescentar} />
          {tracar ? (
            <>
              <p className="cptu-nota">Clique em duas verticais para traçar o corte; fora delas, o clique põe um vértice livre.</p>
              <div className="cptu-analise-acoes-da-linha">
                <button type="button" className="botao secundario" disabled={vertices.length === 0}
                  onClick={() => setVertices((v) => v.slice(0, -1))}>Desfazer o último ponto</button>
                <button type="button" className="botao secundario" disabled={vertices.length === 0}
                  onClick={() => { setVertices([]); setDe(''); setAte(''); }}>Limpar</button>
              </div>
              <div className="cptu-analise-de-ate" role="group" aria-label="A linha pelas verticais">
                <label className="campo">
                  <span className="rotulo">De</span>
                  <select value={de} onChange={(e) => deAte(e.target.value, ate)}>
                    <option value="">—</option>
                    {uteis.map((v) => <option key={v.codigo} value={v.codigo}>{v.codigo}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span className="rotulo">Até</span>
                  <select value={ate} onChange={(e) => deAte(de, e.target.value)}>
                    <option value="">—</option>
                    {uteis.map((v) => <option key={v.codigo} value={v.codigo}>{v.codigo}</option>)}
                  </select>
                </label>
              </div>
            </>
          ) : null}
          {linhas.length > 0 && (
            <div className="cptu-analise-linhas" role="group" aria-label="Linhas de corte gravadas">
              {linhas.map((l) => (
                <span key={l.nome} className="cptu-analise-linha">
                  <Link className={`cptu-analise-ficha${gravada?.nome === l.nome && !provisoria ? ' sel' : ''}`}
                    aria-current={gravada?.nome === l.nome && !provisoria ? 'true' : undefined}
                    to={rotaDaAnalise(c.campanha.id, 'mapa', l.nome)} onClick={() => setVertices([])}>
                    {l.nome}
                  </Link>
                  {tracar && podeEditar && editavel && (
                    <button type="button" className="botao discreto" onClick={() => setJanela({ tipo: 'tirar', nome: l.nome })}>
                      Tirar a linha {l.nome}
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {pontos && (
            <p className="cptu-analise-corte" role="status">
              <b>Corte {nome ?? ''}</b> · {inicio} → {fim} · {metrosBr(comprimento)}
              {' · '}
              {respostaAtual?.insuficiente && !calculando
                ? respostaAtual.insuficiente
                : quantas !== null ? `${plural(quantas, 'vertical', 'verticais')} a até ${AFASTAMENTO_DA_LINHA} m` : '…'}
            </p>
          )}
          {acaoGravar && <div className="cptu-analise-gravar">{acaoGravar}</div>}
        </section>

        <section className="cptu-analise-lado-secao" aria-label="Seção 2D">
          <h3 className="cptu-titulo-da-secao">
            Seção 2D{nome ? ` ${nome}` : ''}
            {marcaDaGrandeza && !marcaDaGrandeza.homologada ? (
              <span className="cptu-marca-texto"> ▲ não homologada{marcaDaGrandeza.motivo ? ` — ${marcaDaGrandeza.motivo}` : ''}</span>
            ) : null}
          </h3>
          <div className="cptu-analise-controles">
            <div className="cptu-segmento" role="group" aria-label="Grandeza da seção">
              {ofertas.map((g) => {
                const def = GRANDEZAS_DA_SECAO.find((x) => x.chave === g.chave)!;
                return (
                  <button className="cptu-segmento-opcao" key={g.chave} type="button" aria-pressed={grandeza === g.chave} data-grandeza={g.chave}
                    title={!g.marca.homologada ? `não homologada${g.marca.motivo ? ` — ${g.marca.motivo}` : ''}` : undefined}
                    onClick={() => setGrandeza(g.chave)}>
                    {def.rotulo}{!g.marca.homologada ? ' ▲' : ''}
                  </button>
                );
              })}
            </div>
            <div className="cptu-segmento" role="group" aria-label="Método">
              <button className="cptu-segmento-opcao" type="button" aria-pressed={metodo === 'krigagem'} onClick={() => setMetodo('krigagem')}>Krigagem</button>
              <button className="cptu-segmento-opcao" type="button" aria-pressed={metodo === 'idw'} onClick={() => setMetodo('idw')}>IDW</button>
            </div>
            {metodo === 'krigagem' && (
              <div className="cptu-segmento" role="group" aria-label="Modelo do variograma">
                <button className="cptu-segmento-opcao" type="button" aria-pressed={modelo === 'esferico'} onClick={() => setModelo('esferico')}>Esférico</button>
                <button className="cptu-segmento-opcao" type="button" aria-pressed={modelo === 'exponencial'} onClick={() => setModelo('exponencial')}>Exponencial</button>
              </div>
            )}
            <label className="cptu-analise-opcao">
              <input type="checkbox" checked={comIsolinhas} onChange={(e) => setComIsolinhas(e.target.checked)} />
              {' '}isolinhas{grandeza === 'sbtn' ? ' (Ic 2,60)' : ''}
            </label>
          </div>
          {!pontos ? (
            <p className="cptu-nota cptu-analise-sem-linha">
              {tracar ? 'Trace uma linha no mapa, ou escolha «De» e «Até», para ver a seção.' : linhas.length ? 'Escolha uma linha gravada para ver a seção.' : 'A campanha ainda não tem linha de corte gravada.'}
            </p>
          ) : (
            <>
              {calculando && <p className="cptu-nota" role="status">Calculando a seção…</p>}
              {erroDaConta ? <ErroDaApi erro={erroDaConta} /> : null}
              {respostaAtual && (
                <div className={estreito ? 'rola' : undefined}>
                  <SecaoEspacial resposta={respostaAtual} grandeza={grandezaDaResposta} isolinhas={comIsolinhas}
                    marca={ofertas.find((g) => g.chave === grandezaDaResposta)?.marca ?? null}
                    rotuloDoInicio={nome ? nome.split('–')[0] : 'A'}
                    sobreposicoes={dadosDasSobreposicoes ? { dados: dadosDasSobreposicoes, ligadas, opacidade: sobre.opacidade } : null} />
                </div>
              )}
              {respostaAtual && respostaAtual.noMesmoLugar.length > 0 && respostaAtual.estacoes.length > 0 && (
                respostaAtual.noMesmoLugar
                  .filter((g) => g.some((x) => respostaAtual.estacoes.some((e) => e.codigo === x)))
                  .map((g) => <p key={g.join()} className="cptu-nota cptu-analise-mesmo-lugar">{comE(g)} no mesmo lugar: entram pela média.</p>)
              )}
              {respostaAtual && estacoes.length > 0 && (
                <ControleDasSobreposicoes estado={sobre} aoMudar={mudarSobre} verticais={estacoes} marcas={marcasDasSobreposicoes}
                  escalas={escalas} aoMudarEscala={mudarEscala} dominio={dominioDasFaixas(dadosDasSobreposicoes)} />
              )}
              {respostaAtual && (
                <p className="cptu-nota cptu-analise-parametros">
                  Parâmetros: passo {numeroBr(passos.secao.horizontal, 2)} × {numeroBr(passos.secao.vertical, 2)} m
                  {' · '}{respostaAtual.metodoUsado === 'krigagem' ? `krigagem ${NOME_DO_MODELO[modelo]}` : 'IDW, potência 2'}
                  {' · '}verticais a até {AFASTAMENTO_DA_LINHA} m da linha
                  {passos.aviso ? ` · ${passos.aviso}` : ''}
                  {respostaAtual.naPagina ? ' · calculado na página' : ''}
                  {' — '}escolhas da tela, sem fonte (modelo interpretativo).
                </p>
              )}
            </>
          )}
        </section>
      </div>
      {respostaAtual && respostaAtual.variogramas.length > 0 && (
        <section className="cptu-analise-variograma" aria-label="Variograma">
          <h3 className="cptu-titulo-da-secao">Variograma</h3>
          <GraficoDoVariograma variogramas={respostaAtual.variogramas} metodoUsado={respostaAtual.metodoUsado}
            motivoDoMetodo={respostaAtual.motivoDoMetodo} />
        </section>
      )}
      {janela?.tipo === 'gravar' && pontos && nomeDaNova && (
        <JanelaDaLinha titulo={`Gravar a linha ${nomeDaNova}`} rotulo={`${inicio} → ${fim} · ${metrosBr(comprimento)}`}
          texto={`A linha ${nomeDaNova} vai com o método e o modelo da tela (${metodo === 'krigagem' ? `krigagem ${NOME_DO_MODELO[modelo]}` : 'IDW'}): o relatório desenha a seção com eles.`}
          acao={`Gravar a linha ${nomeDaNova}`} aoFechar={() => setJanela(null)}
          gravar={async (motivo) => {
            const nova: LinhaDeCorte = { nome: nomeDaNova, pontos: pontos.map((p) => ({ latitude: p.latitude, longitude: p.longitude })), metodo, modelo };
            await gravarLinhasDeCorte(c.campanha.id, { linhas: [...linhas, nova], motivo });
            setJanela(null);
            setVertices([]); setDe(''); setAte('');
            await aoMudar();
            navegar(rotaDaAnalise(c.campanha.id, 'mapa', nomeDaNova), { replace: true });
          }} />
      )}
      {janela?.tipo === 'tirar' && (
        <JanelaDaLinha titulo={`Tirar a linha ${janela.nome}`} rotulo="Linha de corte"
          texto="A linha sai da campanha e do relatório; a trilha guarda quando e por quê." acao={`Tirar a linha ${janela.nome}`}
          aoFechar={() => setJanela(null)}
          gravar={async (motivo) => {
            const nomeTirado = janela.nome;
            await gravarLinhasDeCorte(c.campanha.id, { linhas: linhas.filter((l) => l.nome !== nomeTirado), motivo });
            setJanela(null);
            await aoMudar();
            if (nomeGravado === nomeTirado) navegar(rotaDaAnalise(c.campanha.id, 'mapa'), { replace: true });
          }} />
      )}
    </div>
  );
}

/** A janela com motivo da linha de corte (gravar ou tirar) — o molde da `JanelaDeCancelar`. */
function JanelaDaLinha({ titulo, rotulo, texto, acao, aoFechar, gravar }: {
  titulo: string; rotulo: string; texto: string; acao: string; aoFechar(): void; gravar(motivo: string): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useFocoDeVolta(true, '.cptu-analise-ficha', '.cptu-vistas a');
  const falta = motivo.trim().length < 5 ? 'Diga o motivo (pelo menos 5 caracteres).' : null;
  const enviar = async () => {
    if (falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { await gravar(motivo.trim()); } catch (e) { setErro(e); setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo={titulo} rotulo={rotulo} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void enviar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">{texto}</p>
        <Area rotulo="Motivo" rows={3} value={motivo} maxLength={2000}
          dica="Pelo menos 5 caracteres: o motivo vai para a trilha." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Voltar</button>
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>{ocupado ? 'Gravando…' : acao}</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/* ── 3D ─────────────────────────────────────────────────────────────── */

/** Quantas vezes um pedido cancelado por outro (a fila única do worker) se refaz antes de desistir. */
const REFAZER_NO_MAXIMO = 4;

/**
 * Um pedido ao worker que se REFAZ quando outro o cancela (o `CANCELADO` da fila única: o último pedido vence, e a
 * vista 3D faz vários — a grade e as seções da cerca, uma a uma). Devolve null quando a vista já saiu.
 */
async function pedirAteChegar<T>(pedir: () => { resposta: Promise<T>; cancelar(): void }, vivo: () => boolean,
  aoCancelar: (f: () => void) => void): Promise<T | null> {
  for (let vez = 0; vez <= REFAZER_NO_MAXIMO; vez++) {
    if (!vivo()) return null;
    const t = pedir();
    aoCancelar(t.cancelar);
    try {
      return await t.resposta;
    } catch (e) {
      if (!vivo()) return null;
      if (e instanceof Error && e.message === CANCELADO) continue;
      throw e;
    }
  }
  throw new Error('o cálculo do modelo 3D foi interrompido muitas vezes seguidas: recarregue a página');
}

/** A extensão das verticais úteis em planta (m), para o passo de partida da grade. */
function extensaoDas(uteis: readonly VerticalDaAnalise[]): { x: number; y: number } {
  if (!uteis.length) return { x: 0, y: 0 };
  const geo = uteis.map((v) => ({ latitude: v.latitude as number, longitude: v.longitude as number }));
  const plano = planoLocal(geo);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const g of geo) {
    const q = plano.paraPlano(g.latitude, g.longitude);
    minX = Math.min(minX, q.x); maxX = Math.max(maxX, q.x); minY = Math.min(minY, q.y); maxY = Math.max(maxY, q.y);
  }
  return { x: maxX - minX, y: maxY - minY };
}

function Vista3d({ c, verticais, uteis, aoMudar }: {
  c: CampanhaCompleta; verticais: readonly VerticalDaAnalise[]; uteis: readonly VerticalDaAnalise[]; aoMudar(): Promise<void>;
}) {
  const estreito = useEstreito();
  // No celular só consulta, e o 3D só desce depois do toque (R9).
  const [abertoNoCelular, setAbertoNoCelular] = useState(false);
  const aberto = !estreito || abertoNoCelular;
  const linhas: LinhaDeCorte[] = useMemo(() => c.campanha.linhas_corte ?? [], [c.campanha.linhas_corte]);
  const [grandeza, setGrandeza] = useState<GrandezaDaSecao>('sbtn');
  const [metodo, setMetodo] = useState<'idw' | 'krigagem'>('krigagem');
  const [modelo, setModelo] = useState<ModeloDeVariograma>('esferico');
  const [sobre, mudarSobre] = useSobreposicoes();
  const quem = useSessao().eu?.usuarioId ?? null;
  const [ajustes, setAjustes] = useState<AjustesDo3d>(() => ajustesDo3dGuardados(quem));
  // Trocou quem está na sessão (ou ela chegou depois): os ajustes dele.
  useEffect(() => { setAjustes(ajustesDo3dGuardados(quem)); }, [quem]);
  const mudarAjustes = useCallback((a: AjustesDo3d) => { setAjustes(a); guardarAjustesDo3d(quem, a); }, [quem]);
  /* As camadas do 3D (onda 347, §4.6): as gravadas (lidas das verticais) e, pedida, a sugestão do T-ratio — calculada aqui, no
     aparelho, com os parâmetros guardados da janela «Camadas automáticas» (ou os de partida) e a unidade pela zona SBTn
     predominante; nada se grava (RN-269). */
  const { camadas: gravadas, erro: erroDasCamadas } = useCamadasGravadas(c, aberto);
  const pedeSugestao = ajustes.camadas === 'tratio';
  // A janela «Camadas automáticas» gravou parâmetros novos (Tarefa 10, R33): a sugestão recalcula sem recarregar.
  const [versaoDosParametros, setVersaoDosParametros] = useState(0);
  useEffect(() => ouvirParametrosDoTRatio(quem, () => setVersaoDosParametros((x) => x + 1)), [quem]);
  const sugestao = useMemo(() => {
    if (!pedeSugestao) return null;
    const parametros = parametrosDoTRatioGuardados(quem);
    return new Map(uteis.map((v) => [v.codigo, comUnidadePelaZona(sugerirPorTRatio(v.r, parametros).camadas, v.r)
      .map((k) => ({ topo: k.topo, base: k.base, unidade: k.unidade }))]));
    // A versão não entra na conta: é o sinal de que os guardados mudaram.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedeSugestao, uteis, versaoDosParametros, quem]);
  const camadasDoModelo: CamadasDoModelo = useMemo(() => ({ gravadas, tratio: sugestao }), [gravadas, sugestao]);
  const camadasDisponiveis: FonteDasCamadas[] = useMemo(() => [
    'nenhuma', ...(gravadas && gravadas.size ? ['gravadas' as const] : []), ...(uteis.length ? ['tratio' as const] : []),
  ], [gravadas, uteis.length]);
  const comSugestao = pedeSugestao && camadasDisponiveis.includes('tratio');
  // O sistema em que as verticais do modelo foram lançadas (a regra da 338), para os rótulos da malha.
  const sistema = useMemo(() => {
    const codigos = new Set(uteis.map((u) => u.codigo));
    return sistemaDaMalha(c.verticais.filter((v) => v.situacao === 'ativa' && codigos.has(v.codigo))
      .map((v) => ({ latitude: v.latitude, longitude: v.longitude, lancada: v.coordenada_lancada ?? null, fuso: v.coordenada_fuso ?? null })));
  }, [c.verticais, uteis]);
  const [escalas, mudarEscala] = useEscalas();
  const eixoDaSobreposicao = sobre.grandeza ? escalas[sobre.grandeza] ?? null : null;
  /* A grade e as seções chegam JUNTAS, com a grandeza delas: a cerca nunca pinta uma grandeza com a cor de outra. */
  const [modelo3d, setModelo3d] = useState<{ grade: RespostaDaGradeDoTrabalhador; secoes: Record<string, RespostaDaSecao>; grandeza: GrandezaDaSecao } | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [erroDaConta, setErroDaConta] = useState<unknown>(null);
  const [imagem, setImagem] = useState<ImagemDoModelo | null>(null);
  const [motivoSemImagem, setMotivoSemImagem] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);

  const passos = useMemo(() => passosPadrao({ extensao: extensaoDas(uteis), celular: estreito }), [uteis, estreito]);
  const chaveDasLinhas = linhas.map((l) => `${l.nome}:${l.metodo}:${l.modelo}:${l.pontos.map((p) => `${p.latitude},${p.longitude}`).join(';')}`).join('|');

  /* A grade e, uma a uma, as seções das linhas gravadas (M8 da revisão 316.3a: a fila do worker é uma só). */
  useEffect(() => {
    if (!aberto) return undefined;
    let vivo = true;
    let cancelar: (() => void) | null = null;
    setCalculando(true);
    const espera = setTimeout(() => {
      void (async () => {
        try {
          const grade = await pedirAteChegar(() => calcularNoTrabalhador(pedidoDaGrade(uteis, { grandeza, metodo, modelo, passo: passos.grade })),
            () => vivo, (f) => { cancelar = f; });
          if (!grade) return;
          const secoes: Record<string, RespostaDaSecao> = {};
          if (!grade.insuficiente) {
            for (const l of linhas) {
              const comprimento = comprimentoDa(l.pontos);
              const ps = passosPadrao({ comprimento, celular: estreito });
              const s = await pedirAteChegar(() => calcularNoTrabalhador(pedidoDaSecao(uteis, {
                linha: l.pontos, grandeza, metodo: l.metodo, modelo: l.modelo, passoHorizontal: ps.secao.horizontal, passoVertical: ps.secao.vertical,
              })), () => vivo, (f) => { cancelar = f; });
              if (!s) return;
              if (!s.insuficiente) secoes[l.nome] = s;
            }
          }
          if (!vivo) return;
          setModelo3d({ grade, secoes, grandeza });
          setErroDaConta(null);
          setCalculando(false);
        } catch (e) {
          if (vivo) { setErroDaConta(e); setCalculando(false); }
        }
      })();
    }, ESPERA_DO_RECALCULO);
    return () => { vivo = false; clearTimeout(espera); cancelar?.(); };
    // As linhas resumidas pela chave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, uteis, grandeza, metodo, modelo, passos.grade.x, passos.grade.y, passos.grade.z, chaveDasLinhas, estreito]);

  /* A imagem do terreno: só depois da grade que serve, pelo mapa sob demanda (o visual não toca na rede). */
  const temModelo = !!modelo3d && !modelo3d.grade.insuficiente;
  const chaveDasUteis = uteis.map((v) => `${v.codigo}:${v.latitude},${v.longitude}`).join('|');
  useEffect(() => {
    if (!aberto || !temModelo) return undefined;
    const controle = new AbortController();
    let vivo = true;
    setMotivoSemImagem(null);
    void (async () => {
      try {
        const { montarImagemDoTerreno } = await import('../../mapaGeotecnico');
        const limites = limitesDoTerreno(uteis.map((v) => ({ latitude: v.latitude as number, longitude: v.longitude as number })));
        const r = await montarImagemDoTerreno(limites, { sinal: controle.signal });
        if (vivo) setImagem({ imagem: r.imagem, limites: r.limites, credito: r.credito });
      } catch (e) {
        if (vivo) { setImagem(null); setMotivoSemImagem(e instanceof Error ? e.message : String(e)); }
      }
    })();
    return () => { vivo = false; controle.abort(); };
    // As úteis resumidas pela chave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, temModelo, recarga, chaveDasUteis]);

  /* Os gráficos das verticais: as MESMAS faixas da seção, em todas as verticais do modelo; o mesmo painel. */
  const codigos = useMemo(() => (modelo3d ? modelo3d.grade.verticais.map((v) => v.codigo) : []), [modelo3d]);
  const dadosDasSobreposicoes = useMemo(
    () => (sobre.grandeza && codigos.length
      ? sobreposicoesDasVerticais(verticais, { grandeza: sobre.grandeza, codigos, escala: sobre.escala, eixo: eixoDaSobreposicao })
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verticais, sobre.grandeza, sobre.escala, codigos.join(), eixoDaSobreposicao],
  );
  const ligadas = useMemo(() => new Set(codigos.filter((x) => !sobre.desligadas.includes(x))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [codigos.join(), sobre.desligadas]);
  const sobreposicoes = useMemo(() => (dadosDasSobreposicoes ? { dados: dadosDasSobreposicoes, ligadas, opacidade: sobre.opacidade } : null),
    [dadosDasSobreposicoes, ligadas, sobre.opacidade]);
  const marcasDasSobreposicoes = useMemo(() => {
    const m: Partial<Record<GrandezaDaSobreposicao, MarcaDeHomologacao>> = {};
    for (const g of GRANDEZAS_DA_SOBREPOSICAO) m[g.chave] = combinarMarcas(uteis.map((v) => ({ marca: marcaDaColuna(g.coluna, v.r.premissas) })));
    return m;
  }, [uteis]);
  // Com o NA de cada vertical (o do cabeçalho, que as premissas dela espelham; null = não informado), para a superfície do
  // nível d'água do 3D (onda 347, §4.3).
  const pedidoDasVerticais = useMemo(() => {
    const naDe = new Map(c.verticais.filter((v) => v.situacao === 'ativa').map((v) => [v.codigo, v.nivel_agua]));
    return pedidoDaGrade(uteis, { grandeza: modelo3d?.grandeza ?? grandeza, metodo, modelo, passo: passos.grade }).verticais
      .map((v) => ({ ...v, na: naDe.get(v.codigo) ?? null }));
  },
  // As verticais do pedido só mudam com as úteis, a grandeza e o NA.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [uteis, modelo3d?.grandeza, c.verticais]);

  const ofertas = useMemo(() => grandezasDaSecao(uteis, { mostrarNaoHomologadas: true }), [uteis]);
  /* Dois dedos aproximam (316.6): a pinça ouve a caixa do modelo, sem mexer no visual. */
  const caixaDoModelo = useRef<HTMLDivElement>(null);
  usePincaDoModelo3d(caixaDoModelo, aberto && !!modelo3d);
  /* A captura para o relatório (316.6): o controle chega quando o modelo fica pronto. */
  const [controle, setControle] = useState<ControleDoModelo3d | null>(null);

  if (!aberto) {
    return (
      <div className="cptu-analise-3d">
        <p className="cptu-nota">O modelo 3D desce só quando você pede: um dedo gira, dois aproximam.</p>
        <button type="button" className="botao" onClick={() => setAbertoNoCelular(true)}>Abrir o modelo 3D</button>
      </div>
    );
  }

  return (
    <div className="cptu-analise-3d">
      <div className="cptu-analise-controles">
        <div className="cptu-segmento" role="group" aria-label="Grandeza do modelo">
          {ofertas.map((g) => {
            const def = GRANDEZAS_DA_SECAO.find((x) => x.chave === g.chave)!;
            return (
              <button className="cptu-segmento-opcao" key={g.chave} type="button" aria-pressed={grandeza === g.chave} data-grandeza={g.chave}
                title={!g.marca.homologada ? `não homologada${g.marca.motivo ? ` — ${g.marca.motivo}` : ''}` : undefined}
                onClick={() => setGrandeza(g.chave)}>
                {def.rotulo}{!g.marca.homologada ? ' ▲' : ''}
              </button>
            );
          })}
        </div>
        <div className="cptu-segmento" role="group" aria-label="Método">
          <button className="cptu-segmento-opcao" type="button" aria-pressed={metodo === 'krigagem'} onClick={() => setMetodo('krigagem')}>Krigagem</button>
          <button className="cptu-segmento-opcao" type="button" aria-pressed={metodo === 'idw'} onClick={() => setMetodo('idw')}>IDW</button>
        </div>
        {metodo === 'krigagem' && (
          <div className="cptu-segmento" role="group" aria-label="Modelo do variograma">
            <button className="cptu-segmento-opcao" type="button" aria-pressed={modelo === 'esferico'} onClick={() => setModelo('esferico')}>Esférico</button>
            <button className="cptu-segmento-opcao" type="button" aria-pressed={modelo === 'exponencial'} onClick={() => setModelo('exponencial')}>Exponencial</button>
          </div>
        )}
      </div>
      {calculando && <p className="cptu-nota" role="status">{modelo3d ? 'Recalculando o modelo 3D…' : 'Montando o modelo 3D…'}</p>}
      {erroDaConta ? <ErroDaApi erro={erroDaConta} /> : null}
      {erroDasCamadas ? <ErroDaApi erro={erroDasCamadas} /> : null}
      {modelo3d && (
        <div ref={caixaDoModelo} className="cptu-analise-modelo">
          <Modelo3d verticais={pedidoDasVerticais} grade={modelo3d.grade} grandeza={modelo3d.grandeza} linhas={linhas} secoes={modelo3d.secoes}
            imagem={imagem} motivoSemImagem={motivoSemImagem ?? (imagem ? null : 'buscando as telhas…')}
            aoRecarregarImagem={() => setRecarga((x) => x + 1)} celular={estreito} sobreposicoes={sobreposicoes} aoPronto={setControle}
            ajustes={ajustes} aoAjustes={mudarAjustes} camadasDisponiveis={camadasDisponiveis} sistema={sistema}
            camadas={camadasDoModelo} />
        </div>
      )}
      {/* No celular só se consulta: a vista se guarda para o relatório no computador. */}
      {modelo3d && !modelo3d.grade.insuficiente && !estreito && (
        <GuardarAVista c={c} controle={controle} linhas={linhasDoMetodo(modelo3d.grade, modelo3d.grandeza)} aoMudar={aoMudar}
          bloqueio={comSugestao ? MOTIVO_DA_SUGESTAO_NA_VISTA : null} />
      )}
      {modelo3d && !modelo3d.grade.insuficiente && codigos.length > 0 && (
        <ControleDasSobreposicoes estado={sobre} aoMudar={mudarSobre} verticais={codigos} marcas={marcasDasSobreposicoes}
          titulo="Gráficos das verticais no modelo 3D" escalas={escalas} aoMudarEscala={mudarEscala}
          dominio={dominioDasFaixas(dadosDasSobreposicoes)} />
      )}
      {modelo3d && (
        <p className="cptu-nota cptu-analise-parametros">
          Grade de partida: {numeroBr(passos.grade.x, 0)} × {numeroBr(passos.grade.y, 0)} × {numeroBr(passos.grade.z, 2)} m
          {passos.aviso ? ` · ${passos.aviso}` : ''}
          {modelo3d.grade.naPagina ? ' · calculado na página' : ''}
          {' — '}escolhas da tela, sem fonte (modelo interpretativo).
        </p>
      )}
    </div>
  );
}

/**
 * «Guardar esta vista para o relatório» (316.6): a captura do modelo (o papel claro, o norte, a legenda) com a faixa do
 * método embaixo vai para a folha «Vista 3D» do PDF. Só no computador, com `cptu.editar` e a campanha em rascunho ou
 * devolvida — fora disso, à vista e bloqueada com o motivo (P-UI-24). Guardar de novo troca a vista; «Tirar» a tira do
 * relatório (o arquivo fica no acervo).
 */
function GuardarAVista({ c, controle, linhas, aoMudar, bloqueio }: {
  c: CampanhaCompleta; controle: ControleDoModelo3d | null; linhas: readonly string[]; aoMudar(): Promise<void>;
  /** Por que esta vista não vai ao relatório agora (a sugestão de camadas no 3D, RN-269); null, vai. */
  bloqueio: string | null;
}) {
  const { pode, eu } = useSessao();
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const editavel = campanhaEditavel(c.campanha.situacao);
  const podeEditar = pode('cptu.editar');
  const tem = !!c.campanha.vista3d_arquivo_id;
  const texto = tem ? 'Guardar esta vista no lugar da do relatório' : 'Guardar esta vista para o relatório';
  const guardar = async () => {
    if (!controle || ocupado) return;
    setOcupado(true); setErro(null);
    try {
      const captura = await controle.capturar(TAMANHO_DA_CAPTURA);
      const imagem = await comporAVista(captura, linhas);
      const f = new FormData();
      f.append('imagem', imagem, 'vista3d.png');
      await enviarVista3d(c.campanha.id, f);
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  const tirar = async () => {
    if (ocupado) return;
    setOcupado(true); setErro(null);
    try { await tirarVista3d(c.campanha.id); await aoMudar(); } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <div className="cptu-analise-vista-do-relatorio" role="group" aria-label="A vista 3D no relatório">
      {!podeEditar ? (
        <AcaoBloqueada texto={texto} oQueFaz="não guarda a vista 3D no relatório" permissao={PERMISSAO_DE_EDITAR_CPTU}
          persona={eu?.atuandoPor ? eu.nome : null} />
      ) : !editavel ? (
        <AcaoBloqueada texto={texto} motivo={motivoDaCampanhaParada(c.campanha.situacao)} />
      ) : bloqueio ? (
        <AcaoBloqueada texto={texto} motivo={bloqueio} />
      ) : (
        <button type="button" className="botao secundario" disabled={!controle || ocupado} onClick={() => { void guardar(); }}>
          {ocupado ? 'Guardando…' : texto}
        </button>
      )}
      {tem && podeEditar && editavel && (
        <button type="button" className="botao discreto" disabled={ocupado} onClick={() => { void tirar(); }}>Tirar a vista do relatório</button>
      )}
      <p className="cptu-nota" role="status">
        {tem ? 'O relatório tem uma vista 3D guardada: ela sai na folha «Vista 3D», com o método escrito embaixo.'
          : 'O relatório ainda não tem vista 3D: a folha «Vista 3D» só sai com uma vista guardada.'}
      </p>
      <ErroDaApi erro={erro} />
    </div>
  );
}

/* ── Perfis ─────────────────────────────────────────────────────────── */

function VistaPerfis({ c, verticais, uteis }: { c: CampanhaCompleta; verticais: readonly VerticalDaAnalise[]; uteis: readonly VerticalDaAnalise[] }) {
  const [busca] = useSearchParams();
  const nomeGravado = busca.get('linha');
  const gravada = nomeGravado ? (c.campanha.linhas_corte ?? []).find((l) => l.nome === nomeGravado) ?? null : null;
  const [grandeza, setGrandeza] = useState<Grandeza>('qt');
  const [eixo, setEixo] = useState<'profundidade' | 'cota'>(uteis.length >= 2 ? 'cota' : 'profundidade');
  const [passo, setPasso] = useState(0.25);
  const [modo, setModo] = useState<'sobrepostos' | 'lado_a_lado'>('sobrepostos');
  const [destacada, setDestacada] = useState<string | null>(null);
  const [escalas, mudarEscala] = useEscalas();
  /* O zoom do eixo (onda 347, R13), nos valores do eixo: trocar profundidade × cota volta ao eixo inteiro. */
  const [zoom, setZoom] = useState<{ de: number; ate: number } | null>(null);
  const mudarEixo = (e: 'profundidade' | 'cota') => { if (e !== eixo) setZoom(null); setEixo(e); };
  const ordem = useMemo(() => (gravada ? ordemNaLinha(verticais, gravada.pontos) : undefined), [gravada, verticais]);
  const marcas = useMemo(() => new Map(GRANDEZAS_DA_ESTATISTICA.map((g) => [g,
    combinarMarcas(verticais.map((v) => ({ marca: marcaDaColuna(g, v.r.premissas) })))])), [verticais]);

  return (
    <div className="cptu-analise-perfis">
      <div className="cptu-analise-controles">
        <label className="campo">
          <span className="rotulo">Grandeza</span>
          <select value={grandeza} onChange={(e) => setGrandeza(e.target.value as Grandeza)}>
            {GRANDEZAS_DA_ESTATISTICA.filter((g) => marcas.get(g)?.mostrar !== false).map((g) => (
              <option key={g} value={g}>
                {GRANDEZAS[g].rotulo}{GRANDEZAS[g].unidade ? ` (${GRANDEZAS[g].unidade})` : ''}{marcas.get(g)?.homologada === false ? ' ▲ não homologada' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="cptu-segmento" role="group" aria-label="Eixo">
          <button className="cptu-segmento-opcao" type="button" aria-pressed={eixo === 'profundidade'} onClick={() => mudarEixo('profundidade')}>Profundidade</button>
          <button className="cptu-segmento-opcao" type="button" aria-pressed={eixo === 'cota'} onClick={() => mudarEixo('cota')}>Cota</button>
        </div>
        <div className="cptu-segmento" role="group" aria-label="Passo das faixas">
          {[0.1, 0.25, 0.5].map((x) => (
            <button className="cptu-segmento-opcao" key={x} type="button" aria-pressed={passo === x} onClick={() => setPasso(x)}>{numeroBr(x, 2)} m</button>
          ))}
        </div>
        <div className="cptu-segmento" role="group" aria-label="Como desenhar">
          <button className="cptu-segmento-opcao" type="button" aria-pressed={modo === 'sobrepostos'} onClick={() => setModo('sobrepostos')}>Sobrepostos</button>
          <button className="cptu-segmento-opcao" type="button" aria-pressed={modo === 'lado_a_lado'} onClick={() => setModo('lado_a_lado')}>Lado a lado</button>
        </div>
      </div>
      {gravada && modo === 'lado_a_lado' && <p className="cptu-nota">Na ordem das estações da linha {gravada.nome}; as outras pelo código.</p>}
      <PerfisDaCampanha verticais={verticais} grandeza={grandeza} eixo={eixo} modo={modo} passo={passo} ordem={ordem}
        destacada={destacada} aoDestacar={setDestacada} escalas={escalas} aoMudarEscala={mudarEscala} zoom={zoom} aoZoom={setZoom} />
      <PerfilTipico c={c} verticais={verticais} />
    </div>
  );
}
